import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Snackbar, Alert, Box, LinearProgress, IconButton, Typography, Button } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Minimize as MinimizeIcon,
  OpenInFull as OpenInFullIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const BASE_INTERVAL_MS = 5000;
const MAX_INTERVAL_MS = 30000;
const MAX_TOTAL_DURATION_MS = 10 * 60 * 1000; // 10 minutes

const ImportProgressToast = () => {
  const [toastState, setToastState] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const timeoutRef = useRef(null);
  const inFlightRef = useRef(false);
  const stoppedRef = useRef(false);
  const attemptRef = useRef(0);
  const startTimeRef = useRef(null);

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const hardStop = () => {
    clearTimer();
    localStorage.removeItem('activeLeadImportJob');
    setToastState(null);
    setIsVisible(false);
    stoppedRef.current = true;
  };

  const getLeadImportStatus = async () => {
    const response = await api.get('/lms/leads/import/status/', {
      timeout: 5000,
      params: { _: Date.now() },
    });
    return response.data;
  };

  const VALID_JOB_STATUSES = ['queued', 'pending', 'running', 'completed', 'failed'];

  const scheduleNextPoll = () => {
    if (stoppedRef.current) return;
    const delay = Math.min(BASE_INTERVAL_MS * (attemptRef.current + 1), MAX_INTERVAL_MS);
    attemptRef.current += 1;
    timeoutRef.current = setTimeout(pollOnce, delay);
  };

  const pollOnce = async () => {
    if (stoppedRef.current || inFlightRef.current) return;

    // ⛔ Hard stop after 10 minutes total
    if (startTimeRef.current && Date.now() - startTimeRef.current > MAX_TOTAL_DURATION_MS) {
      hardStop();
      return;
    }

    const activeJob = localStorage.getItem('activeLeadImportJob');
    if (!activeJob) {
      hardStop();
      return;
    }

    inFlightRef.current = true;

    try {
      const raw = await getLeadImportStatus();
      const jobStatus = raw?.status;

      // Handle not_found status - job expired or doesn't exist
      if (jobStatus === 'not_found') {
        // If we have a completed/failed state in toastState, keep showing it
        // Otherwise, stop polling
        if (toastState?.status === 'completed' || toastState?.status === 'failed') {
          // Keep the current state visible, but stop polling
          clearTimer();
          setTimeout(() => {
            localStorage.removeItem('activeLeadImportJob');
            setToastState(null);
            setIsVisible(false);
          }, 5000); // Give user 5 more seconds to see the message
        } else {
          hardStop();
        }
        return;
      }

      if (!jobStatus || !VALID_JOB_STATUSES.includes(jobStatus)) {
        hardStop();
        return;
      }

      const status = {
        ...raw,
        job_id: 'active',
        processed_count: Number(raw?.processed_count ?? 0),
        total_rows: Number(raw?.total_rows ?? 0),
        success_count: Number(raw?.success_count ?? 0),
        error_count: Number(raw?.error_count ?? 0),
        error_rows: raw?.error_rows ?? raw?.errors ?? [],
      };

      flushSync(() => {
        setToastState(status);
        setIsVisible(true);
      });

      if (jobStatus === 'completed' || jobStatus === 'failed') {
        clearTimer();
        
        // Don't remove localStorage immediately - keep it so toast stays visible
        // We'll remove it after the timeout
        
        window.dispatchEvent(
          new CustomEvent('leadImportCompleted', {
            detail: {
              status: jobStatus,
              success_count: status.success_count,
              error_count: status.error_count,
              total_rows: status.total_rows,
            },
          })
        );

        // Keep toast visible longer for completed/failed states (15 seconds)
        setTimeout(() => {
          localStorage.removeItem('activeLeadImportJob');
          setToastState(null);
          setIsVisible(false);
        }, 15000); // Increased from 10000 to 15000
      } else {
        scheduleNextPoll();
      }
    } catch (error) {
      // Any non-200, network, 401 → hard stop
      hardStop();
    } finally {
      inFlightRef.current = false;
    }
  };

  const startPolling = () => {
    if (timeoutRef.current || stoppedRef.current) return;
    attemptRef.current = 0;
    startTimeRef.current = Date.now();
    pollOnce(); // immediate first check
  };

  useEffect(() => {
    const activeJob = localStorage.getItem('activeLeadImportJob');
    if (activeJob) startPolling();

    const handleStorage = (e) => {
      if (e.key !== 'activeLeadImportJob') return;
      if (e.newValue) {
        stoppedRef.current = false;
        startPolling();
      } else {
        hardStop();
      }
    };

    const handleImportStart = () => {
      const job = localStorage.getItem('activeLeadImportJob');
      if (job) {
        stoppedRef.current = false;
        startPolling();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const job = localStorage.getItem('activeLeadImportJob');
        if (job && !stoppedRef.current) pollOnce();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('leadImportStarted', handleImportStart);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimer();
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('leadImportStarted', handleImportStart);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleDismissToast = () => {
    hardStop();
  };

  const handleDownloadErrorCSV = () => {
    const errorRows = toastState?.error_rows ?? toastState?.errors ?? [];
    if (!errorRows.length) return;

    const headerSet = new Set();
    errorRows.forEach(({ row }) => {
      if (row && typeof row === 'object') {
        Object.keys(row).forEach((key) => headerSet.add(key));
      }
    });
    const headers = Array.from(headerSet);
    const csvRows = [];
    const escapeValue = (value) => {
      if (value === null || value === undefined) return '';
      let stringValue = value;
      if (Array.isArray(value)) stringValue = value.join('; ');
      else if (typeof value === 'object') stringValue = JSON.stringify(value);
      else stringValue = String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    };

    csvRows.push([...headers, 'error'].map(escapeValue).join(','));
    errorRows.forEach(({ row = {}, error }) => {
      const values = headers.map((header) => escapeValue(row[header]));
      values.push(escapeValue(error || ''));
      csvRows.push(values.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `failed_imports_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const activeJob = localStorage.getItem('activeLeadImportJob');
  // Show toast if visible AND (has active job OR showing completed/failed state)
  // This ensures completion message stays visible even after localStorage is cleared
  const shouldShow = isVisible && (activeJob || toastState?.status === 'completed' || toastState?.status === 'failed');
  if (!shouldShow) return null;

  const isPending = toastState?.status === 'pending' || toastState?.status === 'queued';
  const isRunning = toastState?.status === 'running';
  const isCompleted = toastState?.status === 'completed';
  const isFailed = toastState?.status === 'failed';
  const isUploadingOrStarting = !toastState || isPending;

  const progress =
    toastState?.total_rows > 0
      ? ((toastState.processed_count || 0) / toastState.total_rows) * 100
      : 0;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Alert
        icon={
          isUploadingOrStarting ? <CloudUploadIcon /> :
          isRunning ? <CloudUploadIcon /> :
          isCompleted ? <CheckCircleIcon /> :
          <ErrorIcon />
        }
        severity={
          isUploadingOrStarting ? 'info' :
          isRunning ? 'info' :
          isCompleted ? 'success' :
          'error'
        }
        sx={{
          minWidth: isMinimized && !isUploadingOrStarting ? 200 : 320,
          maxWidth: isMinimized && !isUploadingOrStarting ? 220 : 400,
          '& .MuiAlert-message': { width: '100%' },
        }}
        action={
          <>
            {(isRunning || isUploadingOrStarting) && (
              <IconButton size="small" color="inherit" onClick={() => setIsMinimized((m) => !m)}>
                {isMinimized ? <OpenInFullIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
              </IconButton>
            )}
            {(isCompleted || isFailed) && (
              <IconButton size="small" color="inherit" onClick={handleDismissToast}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        }
      >
        <Box sx={{ width: '100%' }}>
          {isUploadingOrStarting ? (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Uploading…
              </Typography>
              <LinearProgress variant="indeterminate" sx={{ height: 6, borderRadius: 3, mt: 1 }} />
            </>
          ) : isMinimized ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                {toastState.processed_count || 0} / {toastState.total_rows || 0}
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {isRunning
                  ? `${toastState.processed_count || 0} of ${toastState.total_rows || 0} importing`
                  : isCompleted
                  ? `✅ Import Complete: ${toastState.success_count || 0} leads imported successfully`
                  : '❌ Import Failed'}
              </Typography>

              {isRunning && (
                <>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {toastState.success_count || 0} created, {toastState.error_count || 0} errors
                  </Typography>
                </>
              )}

              {isCompleted && (
                <>
                  {toastState.error_count > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
                        ⚠️ {toastState.error_count} row(s) had errors and were skipped.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadErrorCSV}
                        sx={{ mt: 0.5 }}
                      >
                        Download Error CSV ({toastState.error_count} errors)
                      </Button>
                    </>
                  )}
                </>
              )}

              {isFailed && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {toastState.message || 'Import failed. Please check your file and try again.'}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default ImportProgressToast;
